import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';

export interface UseAutoLogoutParams {
  logoutFunction: (checkAndExpired?: boolean) => void;
  timeoutInSeconds: number;
  checkIntervalInSeconds: number;
  autoSignOutName: string;
}

export const DEFAULT_PARAMS: UseAutoLogoutParams = {
  logoutFunction: () => {
    /* do nothing */
  },
  timeoutInSeconds: 60 * 20, // 預設 : 20 分鐘後登出
  checkIntervalInSeconds: 60, // 預設 : 1 分鐘檢查一次
  autoSignOutName: 'auto-sign-out' // 預設
};

const TIME_OUT_START_ACTION_TIME = 1000 * 10; // 30 秒內做操作動作, 重新儲存操作時間
const LOCAL_STORAGE_KEY = 'lastOperationTimestamp'; // local storage 內儲存的 json object，裡面儲存時間的 key name

export default function useAutoLogout(enable: boolean, startTick: boolean, params = DEFAULT_PARAMS): void {
  /* Global & Local State */
  const intervalId = useRef<NodeJS.Timeout>();
  const [isInit, setIsInit] = useState<boolean>(false);
  /* Functions */
  const triggerLogout = (checkAndExpired?: boolean): void => {
    params.logoutFunction(checkAndExpired);
  };
  const getLocalAutoSignOutTimestamp = (): number => {
    if (params.autoSignOutName) {
      const localAutoSignOutName = params.autoSignOutName;
      const localAutoSignOutTimestamp = localStorage.getItem(localAutoSignOutName);
      if (localAutoSignOutTimestamp) {
        const localTokenJson = JSON.parse(localAutoSignOutTimestamp);
        if (LOCAL_STORAGE_KEY in localTokenJson) {
          return localTokenJson[LOCAL_STORAGE_KEY] as number;
        }
      }
    }
    return -1;
  };
  const setLocalAutoSignOutTimestamp = (timestamp?: number): void => {
    if (params.autoSignOutName) {
      const localAutoSignOutName = params.autoSignOutName;
      const lastOperationTimestamp = timestamp || dayjs().unix() * 1000;
      localStorage.setItem(localAutoSignOutName, JSON.stringify({ [LOCAL_STORAGE_KEY]: lastOperationTimestamp }));
    }
  };
  const removeLocalAutoSignOutTimestamp = (): void => {
    if (params.autoSignOutName) {
      const localAutoSignOutName = params.autoSignOutName;
      const localAutoSignOutTimestamp = localStorage.getItem(localAutoSignOutName);
      if (localAutoSignOutTimestamp) {
        localStorage.removeItem(localAutoSignOutName);
      }
    }
  };
  const checkIsExpired = (): void => {
    const now = dayjs().unix() * 1000; // 現在時間
    const last = getLocalAutoSignOutTimestamp(); // 最後操作時間
    if (last > -1) {
      const diff = now - last;
      /* 超過設定的閒置登出時間，即登出 */
      if (diff >= params.timeoutInSeconds * 1000) {
        triggerLogout(true);
        removeLocalAutoSignOutTimestamp();
      }
    } else {
      setLocalAutoSignOutTimestamp();
    }
  };
  const tick = (): void => {
    const now = dayjs().unix() * 1000; // 現在時間
    const last = getLocalAutoSignOutTimestamp(); // 最後操作時間
    if (last > -1) {
      const diff = now - last;
      /* 超過設定的閒置登出時間，即登出 */
      if (diff >= params.timeoutInSeconds * 1000) {
        triggerLogout();
        removeLocalAutoSignOutTimestamp();
      }
    } else {
      setLocalAutoSignOutTimestamp();
    }
  };
  const startSubscribeTimer = (): void => {
    if (intervalId.current) clearInterval(intervalId.current);
    intervalId.current = undefined;
    intervalId.current = setInterval(tick, params.checkIntervalInSeconds * 1000);
  };
  const clearSubscribeTimer = (): void => {
    if (intervalId.current) clearInterval(intervalId.current);
    intervalId.current = undefined;
  };
  const debounceSetLastOperationTime = debounce(() => {
    setLocalAutoSignOutTimestamp();
  }, TIME_OUT_START_ACTION_TIME);
  const subscribeTimer = (): void => {
    debounceSetLastOperationTime();
  };
  const subscribeDomEvent = (): void => {
    if (params.timeoutInSeconds <= 0) return;
    window.document.addEventListener('mousemove', subscribeTimer);
    window.document.addEventListener('keydown', subscribeTimer);
  };
  const unsubscribeDomEvent = (): void => {
    window.document.removeEventListener('mousemove', subscribeTimer);
    window.document.removeEventListener('keydown', subscribeTimer);
  };
  /* Main */
  useEffect(() => {
    setIsInit(true);
    return () => {
      clearSubscribeTimer();
      unsubscribeDomEvent();
      removeLocalAutoSignOutTimestamp();
    };
  }, []);
  useEffect(() => {
    if (enable && !startTick) {
      clearSubscribeTimer();
      unsubscribeDomEvent();
      removeLocalAutoSignOutTimestamp();
    }
    if (!isInit) return;
    if (enable && startTick) {
      /* 初始化的時候檢查狀態，超過時間也登出 */
      checkIsExpired();
      startSubscribeTimer();
      subscribeDomEvent();
      setLocalAutoSignOutTimestamp();
    }
  }, [enable, startTick]);
}
